import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  EventEmitter,
  Output,
  OnDestroy,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import Highcharts from 'projects/gtw-elements/src/app/core/highcharts-init';
import { NavigationStart, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { isString, merge } from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { ExDashboardService } from '../../../core/executive-dashboards/ex-dashboard.service';
import { ExDashboardEventService } from '../../../core/executive-dashboards/services/event.service';
import { Dashboard } from '../../../core/executive-dashboards/models/Dashboard';
import { DashboardOptions } from '../../../core/executive-dashboards/models/DashboardOptions';
import { SubmissionService } from '../../submission-data/submission.service';
import { Submission } from '../../models/submission.model';
import { FilterObj } from '../../../workspace/data-filters/data-filters.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
/**
 * Chart-click events broadcast by Highcharts handlers (configured in the
 * dashboard JSON via `componentSettings.eventMessage`). Keeping them in one
 * place lets `ngOnDestroy` flush the `BehaviorSubject`'s replay cache so a
 * stale point payload doesn't auto-reopen the popup the next time the
 * dashboard is mounted (for example when the user navigates away and back).
 */
const CHART_CLICK_EVENTS = [
  'efile:get-form-error-list',
  'efile:get-entity-by-count',
  'efile:get-diagnostics-count',
] as const;

const EFILE_DASHBOARD_ID = 'd5chqs';

/**
 * Column definitions for the chart-click popup grid. Direct port of
 * `efileFactory.errorListColumns` / `formListColumns` / `entityListColumns` /
 * `diagListColumns` / `submissionListColumns` (see ui/app/components/efile/EfileService.js
 * L59-L103). DashboardPopupComponent reads `columns` from its row payload and
 * renders one cell per entry.
 */
const ERROR_LIST_COLUMNS = [
  { title: 'Entity', data: 'ENTITY' },
  { title: 'Error Desc', data: 'ERROR_DESC' },
  { title: 'Error Type', data: 'ERROR_TYPE' },
  { title: 'Form', data: 'FORM' },
  { title: 'Form Sub', data: 'FORM_SUB' },
  { title: 'Key Desc', data: 'KEY_DESC' },
  { title: 'Parent Element Name', data: 'PARENT_ELEMENT_NAME' },
  { title: 'Parent SEQ', data: 'PARENT_SEQ_NO' },
  { title: 'Table Name', data: 'TABLE_NAME' },
];
const FORM_LIST_COLUMNS = [
  { title: 'Entity', data: 'ENTITY' },
  { title: 'Form', data: 'IRSFORM' },
  { title: 'Form Sub', data: 'FORM_SUB' },
  { title: 'Parent Element Name', data: 'PARENT_ELEMENT_NAME' },
  { title: 'Source', data: 'SRC' },
];
const ENTITY_LIST_COLUMNS = [
  { title: 'Entity', data: 'ENTITY' },
  { title: 'Form', data: 'IRSFORM' },
  { title: 'Form Sub', data: 'FORM_SUB' },
  { title: 'Parent Element Name', data: 'PARENT_ELEMENT_NAME' },
];
const DIAG_LIST_COLUMNS = [
  { title: 'Entity', data: 'ENTITY' },
  { title: 'Element Name', data: 'ELEMENT_NAME' },
  { title: 'Line No', data: 'LINE_NO' },
  { title: 'Source System', data: 'SOURCE_SYSTEM' },
  { title: 'User Name', data: 'USERNAME' },
];
// Per legacy `efileFactory.submissionListColumns` (EfileService.js L86-103)
// every file column was rendered through `renderDownloadLink` so the cell
// text became a clickable `<a ng-click="ctrl.downloadFile('<type>','<irsId>')">`.
// The Acknowledgement File column also picked up a red text class when the
// value was the string "No". The migrated dashboard-popup template reads
// `downloadType` / `ackRedOnNo` here to reproduce that anchor + red styling.
const SUBMISSION_LIST_COLUMNS = [
  { title: 'Entity', data: 'IRS_ID' },
  { title: 'Manifest File', data: 'MANIFEST_EXISTS', downloadType: 'manifest' },
  { title: 'Acknowledgement File', data: 'ACKNOWLEDGEMENT_EXISTS', downloadType: 'ack', ackRedOnNo: true },
  { title: 'Submission Receipt', data: 'SUBMISSION_RECEIPT_EXISTS', downloadType: 'receipt' },
  { title: 'Submission Zip', data: 'ZIP_FILE_NAME', downloadType: 'zip' },
  { title: 'IFA File', data: 'IFA_FILE', downloadType: 'ifa' },
  { title: 'Submission Indicator', data: 'ENVIRONMENT' },
  { title: 'Accepted', data: 'ACCEPTED' },
  { title: 'Submitted By', data: 'CREATED_BY' },
  { title: 'Submitted Date', data: 'CREATED_TS', isDate: true },
];

function camelize(str: string): string {
  return String(str || '')
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word: string, index: number) =>
      index === 0 ? word.toUpperCase() : word.toLowerCase(),
    )
    .replace(/\s+/g, '');
}

@Component({
    selector: 'gtw-efile-elements-dashboard',
    templateUrl: './efile-elements-dashboard.component.html',
    styleUrls: ['./efile-elements-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EfileElementsDashboardComponent implements OnInit, OnDestroy, OnChanges {
  private readonly subs = new Subscription();
  private dashboardLoadSub?: Subscription;
  private dashboardSheetSub?: Subscription;

  @ViewChild('dashboardRoot', { static: true })
  dashboardRoot?: ElementRef<HTMLElement>;

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  @Input() submission: Submission | any = {};
  @Input() taxYear: any;

  private isInitialized = false;

  // Dashboard state mirrors ExDashboardComponent but scoped to a single submission.
  dashboardLayoutLoading = false;
  dashboardLoaded = false;
  errorMessage = '';
  dashboardOptions: DashboardOptions = {
    dashboard_alert_text: '',
    dashboard_desc: '',
    dashboard_id: '',
    dashboard_key: '',
    dashboard_name: '',
    dashboard_type: '',
    components: [],
    data_sheets: [],
    filters: [],
  };
  dashboard!: Dashboard;

  private priorFilters: any = null;
  private priorFiltersRestored = false;

  // Handle for the deferred `loadDashboard()` macrotask. `switchToTab` always
  // lands the user on `/sub/:id/dashboard` first, so opening a submission and
  // immediately clicking a sidebar entry like "Return Review" used to fire
  // the dashboard config call AND every per-sheet `loadJsonObject?action_code=…`
  // (e.g. i6k670 / 89lhyq / zqe86k) before the host could unmount us. By
  // deferring to a macrotask we give the in-flight sub-state change a chance
  // to tear this component down via `ngOnDestroy` BEFORE we kick off the
  // wasted HTTP roundtrips. `clearTimeout` in `ngOnDestroy` is the gate.
  private deferredLoadHandle: number | null = null;
  private chartReflowHandles: number[] = [];
  private chartReadinessRetryHandle: number | null = null;
  private chartReadinessRetryAttempts = 0;
  private readonly maxChartReadinessRetryAttempts = 8;
  /** Reflow when the dashboard pane's box changes (flex / scroll / sidebar). */
  private dashboardResizeObserver: ResizeObserver | null = null;
  /** Reflow when the pane becomes visible after being off-screen. */
  private dashboardVisibilityObserver: IntersectionObserver | null = null;
  /** Browser `window.setTimeout` handle (number); avoid Node `Timeout` typing mismatch. */
  private layoutReflowDebounceId: number | null = null;
  private readonly layoutReflowDebounceMs = 80;
  /**
   * Identity of the params combination currently being fetched (or `null`
   * when nothing is in flight). Built from `submissionId|taxYear` — when
   * `loadDashboard` is invoked again with the same key we skip the duplicate
   * call. Reset to `null` once the full sheet pipeline resolves (or errors).
   * This is the dashboard-side guard against the multiple-fire scenarios
   * (`ngOnChanges` racing the `ngOnInit` deferred load, parent-input
   * coalescence in a single CD pass, etc.).
   */
  private inFlightKey: string | null = null;

  // ---------------------------------------------------------------------------
  // Chart-click popup state. The legacy EfileDashboardController listens to
  // four `$rootScope` events fired by Highcharts point-click handlers and
  // opens `dashboard-popup.html` via `$uibModal.open`. In Angular we listen
  // to `ExDashboardEventService` (the migrated equivalent) and render
  // <gtw-efile-dashboard-popup> as an in-app modal overlay.
  // ---------------------------------------------------------------------------
  showPopup = false;
  popupRowData: any = null;

  /**
   * Second skeleton row: two half-width chart placeholders (paired with the
   * full-width row above in the template = 3 compartments total).
   */
  readonly skeletonBottomRowSlots: number[] = [0, 1];

  /**
   * True while *any* part of the dashboard pipeline is still in flight —
   * either the dashboard configuration (`dashboardLayoutLoading`) or the
   * per-sheet data fetches (`!dashboardLoaded`). Drives the per-chart
   * loader overlays so users opening a submission from the submission list
   * see a spinner inside every chart card until that chart's data lands.
   */
  get chartsLoading(): boolean {
    return this.dashboardLayoutLoading || !this.dashboardLoaded;
  }

  /**
   * Per-tile loader gate. The previous implementation used a single
   * `chartsLoading` flag tied to "all sheets done", which dropped every
   * overlay in the same frame and let Highcharts mount on a card whose
   * box was still settling. Worse, tiles that short-circuited inside
   * `ex-component-resolver` (no data / strategy errored / unknown
   * component_type) sat behind a global spinner until the slowest sheet
   * resolved.
   *
   * This method ties the loader to the specific `srcSheet` the tile
   * consumes:
   *   - dashboard config not loaded yet → still loading
   *   - tile has no `srcSheet` (self-managed components like
   *     `projectReview`) → not loading once config landed
   *   - the tile's sheet fired `:loaded` or `:error` → not loading
   *   - otherwise → still loading
   *
   * Tiles flip independently as their sheet resolves, so a slow sheet
   * cannot keep the rest of the dashboard's tiles spinning.
   */
  isComponentLoading(component: any): boolean {
    if (this.dashboardLayoutLoading) {
      return true;
    }
    if (!this.dashboard) {
      return true;
    }
    const srcSheet = component?.srcSheet;
    if (!srcSheet) {
      return false;
    }
    if (this.resolvedSheets.has(srcSheet)) {
      return false;
    }
    return true;
  }

  /**
   * Names of every data sheet that has emitted a terminal event
   * (`:loaded` or `:error`). Used by `isComponentLoading` to decide
   * which chart-shells should still render their loader overlay.
   * Cleared on every fresh `loadDashboard()` call.
   */
  private resolvedSheets = new Set<string>();
  private sheetEventSubs: Subscription[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private dashboardService: ExDashboardService,
    private submissionService: SubmissionService,
    private dashboardEventService: ExDashboardEventService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.hydrateSubmissionFromService();
    this.setupSubmissionFilterContext();
    this.subscribeChartClickEvents();
    this.subscribeRouterNavigation();
    // Defer the dashboard load by one macrotask so a fast sub-state change
    // (user lands on `/dashboard` and immediately clicks a sidebar entry
    // like "Return Review") tears this component down before we kick off
    // the dashboard-config + per-sheet loadJsonObject calls. Without this
    // defer the wasted i6k670 / 89lhyq / zqe86k requests show up in the
    // network tab right next to the Return Review's siao78 + grid calls.
    this.deferredLoadHandle = window.setTimeout(() => {
      this.deferredLoadHandle = null;
      this.loadDashboard();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Sub-state switches (e.g. moving from `dashboard` -> `overrides`) destroy
    // this component via `*ngSwitchCase`, but if a host re-uses the same
    // dashboard instance and just changes the submission, we still want any
    // stale chart-click popup hidden so the user doesn't see it overlaying
    // the new submission's data.
    //
    // `submission` is the only trigger for a dashboard reload. The efile
    // dashboard passes only `submission_id` to every sheet API call
    // (`Dashboard.loadSheets` is called with `strictParams: true`), so a
    // `taxYear` re-bind from the parent never changes which data is fetched.
    // Triggering a reload on taxYear caused a second full config + sheet
    // request whenever `globalParamsChanged$` fired after the initial load
    // completed and flushed `inFlightKey` — the "same call twice" symptom.
    const submissionChanged =
      !!changes['submission'] && !changes['submission'].firstChange;
    if (submissionChanged) {
      this.forceClosePopup();
      this.refreshDashboardForCurrentSubmission();
    }
  }

  ngOnDestroy(): void {
    // Cancel a deferred dashboard load that hasn't fired yet — this is the
    // gate that suppresses the "fast click-through" sheet calls (i6k670,
    // 89lhyq, zqe86k, …) when the user opens a submission and immediately
    // navigates to Return Review / Add-Edit / etc.
    if (this.deferredLoadHandle != null) {
      window.clearTimeout(this.deferredLoadHandle);
      this.deferredLoadHandle = null;
    }
    this.inFlightKey = null;
    this.clearChartReflowHandles();
    this.disconnectDashboardLayoutObservers();
    this.unsubscribeSheetEvents();
    this.resolvedSheets.clear();
    // Hide popup before tearing down so the overlay doesn't flash open on
    // the next mount via the BehaviorSubject's replay (see flushChartClickReplay).
    this.forceClosePopup();
    this.flushChartClickReplay();
    this.subs.unsubscribe();
    this.dashboardLoadSub?.unsubscribe();
    this.dashboardSheetSub?.unsubscribe();
    // Restore any previous activeScreen.filters we overwrote, so sibling screens
    // (submission list grid) keep working after this dashboard unmounts.
    this.restoreFilterContext();
  }

  /**
   * Closes the popup the moment a routed navigation begins. The dashboard
   * is rendered via `*ngSwitchCase="'dashboard'"` inside `<submission-detail>`
   * so leaving the dashboard tab also unmounts this component and the popup
   * overlay. Listening to `NavigationStart` here is a defensive belt-and-
   * suspenders fix for the case where the host outlet decides to keep us
   * mounted across the URL transition.
   */
  private subscribeRouterNavigation(): void {
    this.subs.add(
      this.router.events
        .pipe(filter((e) => e instanceof NavigationStart))
        .subscribe(() => this.forceClosePopup()),
    );
  }

  /**
   * `ExDashboardEventService` replays the last payload **per event name**
   * (not only the last event globally). Chart-click listeners would still
   * see the previous session's point on reopen; without this flush, the
   * next open could immediately re-open the popup. Pushing `null` updates
   * the cached payload for those names so subscribers early-return on
   * `!data` / `data.name === null`.
   */
  private flushChartClickReplay(): void {
    for (const eventName of CHART_CLICK_EVENTS) {
      this.dashboardEventService.broadcast(eventName, null);
    }
  }

  private forceClosePopup(): void {
    if (!this.showPopup) return;
    this.showPopup = false;
    this.popupRowData = null;
    this.cdr.markForCheck();
  }

  /**
   * The dashboard is rendered via *ngSwitch inside <submission-detail>, which
   * doesn't forward inputs into the sub-component. Fall back to the shared
   * `SubmissionService.activeSubmission` when no @Input value is provided —
   * that matches legacy `EfileFactory.selectedSubmission` semantics.
   */
  private hydrateSubmissionFromService(): void {
    if (!this.submission || !Object.keys(this.submission).length) {
      const active = this.submissionService.activeSubmission;
      if (active) {
        this.submission = active;
      }
    }
    if (!this.taxYear) {
      this.taxYear = this.submission?.TAX_YEAR;
    }
  }

  private getSubmissionId(): any {
    return this.submission?.SUBMISSION_ID
      ?? this.submission?.submission_id
      ?? this.submission?.SUBMISSIONID
      ?? null;
  }

  /**
   * Mirrors legacy EfileDashboardController which overwrites
   * `workspaceFactory.activeScreen.filters` with a submission-scoped
   * filter set so JsonObjectFactoryService picks submission_id up
   * automatically for every sheet fetch.
   */
  private setupSubmissionFilterContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen;
    if (!activeScreen) return;
    const submissionId = this.getSubmissionId();

    const filterParams: any = {
      submission_id: submissionId,
      tax_year: this.taxYear || this.submission?.TAX_YEAR,
    };

    // Cache whatever was there so we can put it back on destroy.
    if (!this.priorFilters) {
      this.priorFilters = activeScreen.filters;
    }

    // Use a real `FilterObj` instance (not a plain-object shim) so the
    // shared `data-filters` component — which `instanceof`-checks aside,
    // freely calls `getFilters()`, `toggleFilters()`, `setFilters()`, etc.
    // when the workspace re-mounts a normal screen on top of the same
    // `activeScreen.filters` slot — never blows up with
    // `getFilters is not a function`. The previous plain-object stub
    // implemented only `getFilterParams` / `getFilterParamsFromCache`,
    // so `DataFiltersComponent.initFilters` (which dereferences
    // `activeScreen.filters?.loaded` and then immediately calls
    // `getFilters()` on the truthy result) crashed during navigation
    // away from the efile dashboard.
    //
    // We override `getFilterParams` / `getFilterParamsFromCache` so the
    // submission-scoped params are still returned to JsonObjectFactory
    // unchanged — the rest of the FilterObj API (empty filters list,
    // `getFilters() === []`, `toggleFilters()` no-op, etc.) is inherited
    // from the base class.
    const synth = new FilterObj();
    synth.setLoaded(true);
    synth.filterParams = filterParams;
    synth.getFilterParams = function () {
      return this.filterParams;
    };
    synth.getFilterParamsFromCache = function () {
      return this.filterParams;
    };
    activeScreen.filters = synth;
  }

  private restoreFilterContext(): void {
    if (this.priorFiltersRestored) return;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen;
    if (activeScreen && this.priorFilters !== null) {
      activeScreen.filters = this.priorFilters;
    }
    this.priorFiltersRestored = true;
  }

  private loadDashboard(): void {
    // Re-resolve just-in-time because tab-open from edit icon can race with
    // active-submission hydration.
    this.hydrateSubmissionFromService();
    this.setupSubmissionFilterContext();
    const submissionId = this.getSubmissionId();
    if (!submissionId) {
      this.errorMessage = 'No submission selected.';
      this.dashboardLoaded = false;
      this.dashboardLayoutLoading = false;
      this.inFlightKey = null;
      this.cdr.markForCheck();
      return;
    }

    // Identical-params guard: when an in-flight pipeline is already loading
    // the same submission + tax-year combo, skip the duplicate request.
    // Different params still proceed because they tear down the previous
    // subscriptions below.
    const key = `${submissionId}|${this.taxYear ?? ''}`;
    if (this.inFlightKey === key) {
      return;
    }
    this.inFlightKey = key;
    this.disconnectDashboardLayoutObservers();
    // Reset per-tile readiness — every tile must show its loader again
    // until its sheet (or :error) resolves for the new submission.
    this.unsubscribeSheetEvents();
    this.resolvedSheets.clear();

    this.dashboardLoadSub?.unsubscribe();
    this.dashboardSheetSub?.unsubscribe();
    this.dashboardLayoutLoading = true;
    this.dashboardLoaded = false;
    this.errorMessage = '';
    this.cdr.markForCheck();

    this.dashboardLoadSub = this.dashboardService.loadDashboardObservable(EFILE_DASHBOARD_ID).subscribe({
        next: (response: any) => {
          const options: DashboardOptions = response?.dashboard;
          if (!options) {
            this.errorMessage = 'Dashboard configuration is empty.';
            this.dashboardLayoutLoading = false;
            this.dashboardLoaded = false;
            this.inFlightKey = null;
            this.cdr.markForCheck();
            return;
          }
          this.dashboardOptions = options;
          this.parseExtraOptions();
          this.applyEfileChartLabelDefaults();

          this.dashboard = this.dashboardService.createOrUpdateDashboard(
            options.dashboard_name,
            options.dashboard_id,
            options.data_sheets,
            options.components,
          );

          // Subscribe to per-sheet :loaded / :error events BEFORE
          // `loadSheets()` actually fires so we don't miss the broadcast.
          // `resolvedSheets` drives `isComponentLoading()` which gates
          // the per-tile chart-loader-overlay individually.
          this.subscribeToSheetEvents();

          this.dashboardLayoutLoading = false;
          this.isInitialized = true;

          if (!this.dashboard.dataSheets?.length) {
            this.dashboardLoaded = true;
            this.inFlightKey = null;
            this.cdr.markForCheck();
            this.runAfterDomReady(() => {
              this.scheduleChartReflow();
              this.attachDashboardLayoutObservers();
            });
            return;
          }

          this.dashboardSheetSub = this.dashboard
            .loadSheets(
              { submission_id: String(submissionId) },
              true
            )
            .subscribe({
              next: () => {
                for (const sheet of this.dashboard.dataSheets) {
                  sheet.isLoaded = true;
                }
                this.dashboardLayoutLoading = false;
                this.dashboardLoaded = true;
                this.inFlightKey = null;
                this.cdr.markForCheck();
                // Flush removal of per-chart loader overlays before reflow;
                // otherwise Highcharts can paint at stale/zero box (fixed on click).
                this.runAfterDomReady(() => {
                  this.scheduleChartReflow();
                  this.attachDashboardLayoutObservers();
                });
              },
              error: (err) => {
                console.error('[EfileDashboard] Sheet load failed:', err);
                this.dashboardLayoutLoading = false;
                this.dashboardLoaded = true;
                this.errorMessage = 'Could not load dashboard data.';
                this.inFlightKey = null;
                this.cdr.markForCheck();
                this.runAfterDomReady(() => {
                  this.scheduleChartReflow();
                  this.attachDashboardLayoutObservers();
                });
              },
            });
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('[EfileDashboard] Dashboard config load failed:', err);
          this.dashboardLayoutLoading = false;
          this.dashboardLoaded = false;
          this.errorMessage = 'Could not load dashboard configuration.';
          this.inFlightKey = null;
          this.cdr.markForCheck();
        },
      });

    if (this.dashboardLoadSub) {
      this.subs.add(this.dashboardLoadSub);
    }
    if (this.dashboardSheetSub) {
      this.subs.add(this.dashboardSheetSub);
    }
  }

  /**
   * Wire dashboard-scoped subscriptions to every sheet's terminal events.
   * `Dashboard.ts` broadcasts `sheet-<name>:loaded` on success and
   * `sheet-<name>:error` when the HTTP fails or `callSuccess !== '1'`.
   * Either outcome marks the sheet as resolved so `isComponentLoading`
   * can drop that tile's loader overlay independently of the rest of the
   * dashboard, while late-mounting Highcharts instances still benefit
   * from the chart-local readiness retry chain.
   */
  private subscribeToSheetEvents(): void {
    this.unsubscribeSheetEvents();
    const sheets = this.dashboard?.dataSheets || [];
    for (const sheet of sheets) {
      const name = sheet?.name;
      if (!name) continue;
      const loadedSub = this.dashboardEventService
        .on('sheet-' + name + ':loaded')
        .subscribe(() => {
          if (!this.resolvedSheets.has(name)) {
            this.resolvedSheets.add(name);
            this.cdr.markForCheck();
          }
        });
      const errorSub = this.dashboardEventService
        .on('sheet-' + name + ':error')
        .subscribe(() => {
          if (!this.resolvedSheets.has(name)) {
            this.resolvedSheets.add(name);
            this.cdr.markForCheck();
          }
        });
      this.sheetEventSubs.push(loadedSub, errorSub);
    }
  }

  private unsubscribeSheetEvents(): void {
    for (const sub of this.sheetEventSubs) {
      sub.unsubscribe();
    }
    this.sheetEventSubs = [];
  }

  /**
   * OnPush + `*ngIf` loader teardown applies after the current microtask.
   * Run reflow after the chart cards have their final box in the layout tree.
   *
   * Highcharts also mounts while card widths are still settling (especially the
   * second dashboard row); `scheduleChartReflow` dispatches resize + scoped
   * `chart.reflow()` so half-width charts paint without a click.
   */
  private runAfterDomReady(fn: () => void): void {
    queueMicrotask(() => {
      this.cdr.detectChanges();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => fn());
      });
    });
  }

  private scheduleChartReflow(): void {
    // Each `gtw-ex-chart` runs its own 5-step reflow chain, ResizeObserver,
    // and IntersectionObserver — so a multi-tick `dispatchEvent('resize')`
    // staircase from this layer is redundant noise. One immediate rAF pass
    // catches the common "loader overlay just removed, layout settling"
    // case; the 250ms / 500ms passes cover flex-collapse and resolver delay.
    // 1000ms / 1500ms catch late mounts and layout under CPU throttle (aligns
    // with ex-chart's internal reflow window).
    this.clearChartReflowHandles();
    this.chartReadinessRetryAttempts = 0;
    requestAnimationFrame(() => {
      this.nudgeHostedChartsWithReadinessRetry();
    });
    const h1 = window.setTimeout(() => {
      this.nudgeHostedChartsWithReadinessRetry();
    }, 250);
    const h2 = window.setTimeout(() => {
      this.nudgeHostedChartsWithReadinessRetry();
    }, 500);
    const h3 = window.setTimeout(() => {
      this.nudgeHostedChartsWithReadinessRetry();
    }, 1000);
    const h4 = window.setTimeout(() => {
      this.nudgeHostedChartsWithReadinessRetry();
    }, 1500);
    this.chartReflowHandles.push(h1, h2, h3, h4);
  }

  private nudgeChartsFromLayout(): void {
    this.nudgeHostedChartsWithReadinessRetry();
    this.cdr.markForCheck();
  }

  private nudgeHostedChartsWithReadinessRetry(): void {
    window.dispatchEvent(new Event('resize'));
    const hasUnreadyCharts = this.reflowHostedHighcharts();
    if (hasUnreadyCharts) {
      this.scheduleChartReadinessRetry();
    }
  }

  private scheduleChartReadinessRetry(): void {
    if (this.chartReadinessRetryHandle != null) {
      return;
    }
    if (this.chartReadinessRetryAttempts >= this.maxChartReadinessRetryAttempts) {
      return;
    }
    const delayMs = Math.min(180 + this.chartReadinessRetryAttempts * 140, 700);
    this.chartReadinessRetryHandle = window.setTimeout(() => {
      this.chartReadinessRetryHandle = null;
      this.chartReadinessRetryAttempts += 1;
      this.nudgeHostedChartsWithReadinessRetry();
    }, delayMs);
  }

  private debouncedNudgeChartsFromLayout(): void {
    if (this.layoutReflowDebounceId != null) {
      window.clearTimeout(this.layoutReflowDebounceId);
    }
    this.layoutReflowDebounceId = window.setTimeout(() => {
      this.layoutReflowDebounceId = null;
      this.nudgeChartsFromLayout();
    }, this.layoutReflowDebounceMs);
  }

  /**
   * Observe real layout / visibility of the dashboard root so Highcharts
   * reflows after flex height and late-mounted resolvers settle (not only 500ms timers).
   */
  private attachDashboardLayoutObservers(): void {
    this.disconnectDashboardLayoutObservers();
    const el = this.dashboardRoot?.nativeElement;
    if (!el) {
      return;
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.dashboardResizeObserver = new ResizeObserver(() => {
        this.debouncedNudgeChartsFromLayout();
      });
      this.dashboardResizeObserver.observe(el);
    }

    if (typeof IntersectionObserver !== 'undefined') {
      this.dashboardVisibilityObserver = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              this.debouncedNudgeChartsFromLayout();
              break;
            }
          }
        },
        { root: null, rootMargin: '0px', threshold: [0, 0.01, 0.1] },
      );
      this.dashboardVisibilityObserver.observe(el);
    }
  }

  private disconnectDashboardLayoutObservers(): void {
    if (this.layoutReflowDebounceId != null) {
      window.clearTimeout(this.layoutReflowDebounceId);
      this.layoutReflowDebounceId = null;
    }
    if (this.dashboardResizeObserver) {
      this.dashboardResizeObserver.disconnect();
      this.dashboardResizeObserver = null;
    }
    if (this.dashboardVisibilityObserver) {
      this.dashboardVisibilityObserver.disconnect();
      this.dashboardVisibilityObserver = null;
    }
  }

  /** Direct reflow for charts under this dashboard (Data Status / Diagnostics half-columns). */
  private reflowHostedHighcharts(): boolean {
    const root = this.dashboardRoot?.nativeElement;
    const charts = (Highcharts as any).charts as any[] | undefined;
    if (!root || !Array.isArray(charts)) {
      return false;
    }
    let hasUnreadyCharts = false;
    let matchedCharts = 0;
    for (const chart of charts) {
      if (!chart?.renderTo || !root.contains(chart.renderTo as Node)) {
        continue;
      }
      matchedCharts += 1;
      try {
        if (!chart.renderer || !chart.options) {
          hasUnreadyCharts = true;
          continue;
        }
        const h = this.measureHostedChartHeight(chart.renderTo as HTMLElement, chart);
        if (h > 40) {
          chart.setSize(null, h, false);
        }
        chart.reflow();
        const renderEl = chart.renderTo as HTMLElement;
        const containerWidth = renderEl?.clientWidth ?? 0;
        const plotWidth = Number(chart.plotWidth ?? 0);
        const plotHeight = Number(chart.plotHeight ?? 0);
        if (containerWidth < 20 || plotWidth < 20 || plotHeight < 20) {
          hasUnreadyCharts = true;
        }
      } catch {
        hasUnreadyCharts = true;
      }
    }
    // Late dynamic mounts: if dashboard is loaded and chart-capable components
    // are expected but no hosted Highcharts instance is registered yet, keep
    // bounded retries alive instead of treating this pass as "ready".
    if (
      matchedCharts === 0 &&
      this.dashboardLoaded &&
      this.expectedHighchartComponents() > 0
    ) {
      hasUnreadyCharts = true;
    }
    return hasUnreadyCharts;
  }

  private expectedHighchartComponents(): number {
    const rows = this.dashboardOptions?.components || [];
    let count = 0;
    for (const row of rows) {
      if (!Array.isArray(row)) continue;
      for (const comp of row) {
        const componentType = String(comp?.component_type || '').toLowerCase();
        const hasChartSettings = !!comp?.extraOptions?.componentSettings?.chart;
        if (
          hasChartSettings ||
          componentType.includes('chart') ||
          componentType.includes('pie') ||
          componentType.includes('bar') ||
          componentType.includes('line') ||
          componentType.includes('area')
        ) {
          count += 1;
        }
      }
    }
    return count;
  }

  private measureHostedChartHeight(renderTo: HTMLElement, chart: any): number {
    const body = renderTo.closest('.gt-dashboard-ex-body') as HTMLElement | null;
    const card = renderTo.closest('.gt-dashboard-ex') as HTMLElement | null;
    const direct = body?.clientHeight ?? 0;
    if (direct > 40) {
      return direct;
    }
    if (card && card.clientHeight > 50) {
      return Math.max(0, card.clientHeight - 35);
    }
    const parent = renderTo.parentElement;
    const parentHeight = parent?.clientHeight ?? 0;
    if (parentHeight > 40) {
      return parentHeight;
    }
    const chartCfgHeight = this.parseChartHeight(chart?.options?.chart?.height);
    if (chartCfgHeight > 40) {
      return Math.max(80, chartCfgHeight - 35);
    }
    return 280;
  }

  private parseChartHeight(raw: unknown): number {
    if (raw == null) {
      return 0;
    }
    const s = String(raw).trim();
    if (!s || (s.endsWith('%') && !/px/i.test(s))) {
      return 0;
    }
    const px = /^(\d+(?:\.\d+)?)\s*px\s*$/i.exec(s);
    if (px) {
      const n = parseFloat(px[1]);
      return !isNaN(n) ? Math.round(n) : 0;
    }
    const n = parseFloat(s);
    return !isNaN(n) ? Math.round(n) : 0;
  }

  private clearChartReflowHandles(): void {
    for (const handle of this.chartReflowHandles) {
      window.clearTimeout(handle);
    }
    this.chartReflowHandles = [];
    if (this.chartReadinessRetryHandle != null) {
      window.clearTimeout(this.chartReadinessRetryHandle);
      this.chartReadinessRetryHandle = null;
    }
    this.chartReadinessRetryAttempts = 0;
  }

  private refreshDashboardForCurrentSubmission(): void {
    // Cancel the macrotask scheduled in `ngOnInit`: an explicit refresh has
    // arrived, so letting the deferred `loadDashboard` fire in addition would
    // produce a duplicate `getDashboardByKey` + per-sheet `loadJsonObject`
    // burst when both paths land on the same submission/tax-year combo.
    if (this.deferredLoadHandle != null) {
      window.clearTimeout(this.deferredLoadHandle);
      this.deferredLoadHandle = null;
    }
    this.hydrateSubmissionFromService();
    this.setupSubmissionFilterContext();
    this.loadDashboard();
  }

  private parseExtraOptions(): void {
    const rows = this.dashboardOptions?.components || [];
    for (const row of rows) {
      if (!Array.isArray(row)) continue;
      for (const comp of row) {
        if (comp.extraOptions && isString(comp.extraOptions)) {
          try {
            comp.extraOptions = safeJsonInput<any>(comp.extraOptions, null, 'efile-elements-dashboard.parse1');
          } catch {
            try {
              comp.extraOptions = JSON.parse(comp.extraOptions.replace(/'/g, '"'));
            } catch (e2) {
              console.warn('[EfileDashboard] Could not parse extraOptions for component:', comp.component_id, e2);
            }
          }
        }
        if (comp?.extraOptions?.kpis) {
          for (const kpi of comp.extraOptions.kpis) {
            if (kpi.extraOptions && isString(kpi.extraOptions)) {
              try {
                kpi.extraOptions = safeJsonInput<any>(kpi.extraOptions, null, 'efile-elements-dashboard.parse2');
              } catch {
                try {
                  kpi.extraOptions = JSON.parse(kpi.extraOptions.replace(/'/g, '"'));
                } catch (e2) {
                  console.warn('[EfileDashboard] Could not parse KPI extraOptions:', kpi.component_id, e2);
                }
              }
            }
          }
        }
      }
    }
  }

  /**
   * Ensures bar/column/line charts show values above points and pie/donut charts
   * use outside labels with connectors (legacy e-file dashboard screenshots).
   * Merged after JSON parse so server config can still override with deeper keys.
   */
  private applyEfileChartLabelDefaults(): void {
    const columnLike = ['column', 'bar', 'line', 'spline', 'area'];
    const pieLike = ['pie', 'donut', 'variablepie'];
    const rows = this.dashboardOptions?.components || [];
    for (const row of rows) {
      if (!Array.isArray(row)) continue;
      for (const comp of row) {
        const eo = comp?.extraOptions;
        if (!eo?.componentSettings) continue;
        const cs = eo.componentSettings;
        // Highcharts 11 draws a breadcrumb path ("COUNT / 1065K1") after a
        // drill. Legacy charts used the boxed "◁ Back to {series}" button.
        cs.drilldown = merge({}, cs.drilldown || {}, {
          breadcrumbs: {
            floating: true,
            showFullPath: false,
            format: '◁ Back to {level.name}',
            relativeTo: 'plotBox',
            position: {
              align: 'right',
              verticalAlign: 'top',
              x: -36,
              y: 4,
            },
            buttonTheme: {
              fill: '#ffffff',
              stroke: '#cccccc',
              'stroke-width': 1,
              padding: 6,
              r: 2,
              style: {
                color: '#333333',
                fontSize: '11px',
                fontWeight: 'normal',
              },
              states: {
                hover: { fill: '#f2f2f2' },
                select: { fill: '#ffffff' },
              },
            },
          },
        });
        const chartType = String(cs.chart?.type || 'column').toLowerCase();

        if (pieLike.includes(chartType)) {
          const outsidePieLabels = {
            enabled: true,
            distance: 30,
            connectorWidth: 1,
            connectorColor: '#888888',
            softConnector: true,
            style: { textOutline: 'none', fontWeight: 'normal', fontSize: '11px' },
            format: '<b>{point.name} : </b> {point.percentage:.2f} %',
          };
          cs.plotOptions = cs.plotOptions || {};
          merge(cs.plotOptions, { series: { dataLabels: { enabled: true } } });
          if (chartType === 'variablepie') {
            merge(cs.plotOptions, {
              variablepie: { dataLabels: { ...outsidePieLabels } },
            });
          } else {
            merge(cs.plotOptions, { pie: { dataLabels: { ...outsidePieLabels } } });
          }
          continue;
        }

        if (columnLike.includes(chartType)) {
          cs.plotOptions = cs.plotOptions || {};
          merge(cs.plotOptions, {
            series: {
              dataLabels: {
                enabled: true,
                crop: false,
                overflow: 'allow',
                inside: false,
                verticalAlign: 'top',
                y: -6,
                style: {
                  fontWeight: 'bold',
                  fontSize: '11px',
                  textOutline: 'none',
                  color: '#000000',
                },
              },
            },
          });
        }
      }
    }
  }

  get submissionBannerText(): string {
    const sub = this.submission || {};
    if (!sub.SUBMITTED_BY) return '';
    const envLabel = sub.IRS_INDICATOR === 'T' ? 'IRS Sandbox' : 'Production Submission';
    const date = sub.SUBMITTED_DATE ? new Date(sub.SUBMITTED_DATE).toLocaleDateString('en-US') : '';
    const status = sub.SUBMISSION_STATUS || '';
    return `Latest Submission was sent to ${envLabel} by ${sub.SUBMITTED_BY} on ${date} and was ${status}`;
  }

  /**
   * Mirrors `EfileDashboardController.vm.showSubmissions` (L1203). Opens the
   * IRS Submissions Status popup with action_code `81j7mv`.
   */
  showSubmissions(): void {
    const ssoId = this.gtwApp?.USER_SETTINGS?.user?.sso_id || '';
    const submissionId = this.getSubmissionId();
    this.openPopup(
      { submission_id: submissionId, sso_id: ssoId },
      '81j7mv',
      SUBMISSION_LIST_COLUMNS,
      'IRS Submissions Status',
    );
  }

  /**
   * Subscribes to the four legacy chart-click events:
   *   `efile:get-form-error-list` (forms/errors stacked-bar by category)
   *   `efile:get-entity-by-count`  (entity drilldown)
   *   `efile:get-diagnostics-count` (diagnostics by type)
   * Each chart fires through `ExDashboardEventService` once the user clicks
   * a Highcharts series point (see `ex-chart.component.ts` L138-L145, where
   * the configured `eventMessage` is broadcast alongside the
   * `component_id`).
   */
  private subscribeChartClickEvents(): void {
    this.subs.add(
      this.dashboardEventService.on('efile:get-form-error-list').subscribe((data: any) => {
        if (!data) return;
        const submissionId = this.getSubmissionId();
        const params: any = {
          submission_id: submissionId,
          source: data.category,
          sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id || '',
          tax_year: this.submission?.TAX_YEAR,
        };
        const seriesName = data?.series?.name;
        if (seriesName === 'ERROR_COUNT') {
          this.openPopup(
            params,
            'imz4j5',
            ERROR_LIST_COLUMNS,
            'IRS Submission Errors for ' + camelize(data.category),
          );
        } else {
          // Forms split by source: DST has its own action code (m0i39c).
          this.openPopup(
            params,
            data.category === 'DST' ? 'm0i39c' : 'up4cq5',
            FORM_LIST_COLUMNS,
            'IRS Submission Forms for ' + camelize(data.category),
          );
        }
      }),
    );

    this.subs.add(
      this.dashboardEventService.on('efile:get-entity-by-count').subscribe((data: any) => {
        const point = isHighchartsPoint(data) ? data : data?.point;
        if (!point || point.name === null) return;
        // Category-axis points often have no `name`; the axis label is
        // `category`. Series name can live on options rather than `series.name`.
        const formName = chartPointLabel(point);
        const entityDesc = chartSeriesLabel(point);
        if (!formName) return;
        const submissionId = this.getSubmissionId();
        const params: any = {
          submission_id: submissionId,
          form_name: formName,
          entity_desc: entityDesc,
          tax_year: this.submission?.TAX_YEAR,
        };
        this.openPopup(params, 'pxwl8n', ENTITY_LIST_COLUMNS, `${formName} Form By Entity Level`);
      }),
    );

    this.subs.add(
      this.dashboardEventService.on('efile:get-diagnostics-count').subscribe((data: any) => {
        if (!data || data.name === null) return;
        const submissionId = this.getSubmissionId();
        const params: any = {
          submission_id: submissionId,
          in_diag_type: data.name,
          tax_year: this.submission?.TAX_YEAR,
        };
        this.openPopup(params, '5hvo2c', DIAG_LIST_COLUMNS, data.name);
      }),
    );
  }

  /**
   * Builds the popup row payload and opens the in-app modal. Direct port of
   * `EfileDashboardController.openPopup` — the action_code becomes part of
   * the api_url so DashboardPopupComponent can hit
   * `loadJsonObject?action_code=…` directly.
   */
  private openPopup(params: any, actionCode: string, columns: any[], title: string): void {
    this.popupRowData = {
      title,
      api_url: `/loadJsonObject?action_code=${actionCode}`,
      params: omitUnsetParams(params),
      columns,
    };
    this.showPopup = true;
    this.cdr.markForCheck();
  }

  closePopup(_event?: any): void {
    this.showPopup = false;
    this.popupRowData = null;
    this.cdr.markForCheck();
  }
}

function isHighchartsPoint(value: any): boolean {
  return !!value && !Array.isArray(value.series) && !!value.series;
}

function chartPointLabel(point: any): string | undefined {
  const named = definedText(point?.name) ?? definedText(point?.options?.name);
  if (named) {
    return named;
  }
  if (typeof point?.category === 'string') {
    return definedText(point.category);
  }
  const categories = point?.series?.xAxis?.categories;
  if (Array.isArray(categories) && typeof point?.x === 'number') {
    return definedText(categories[point.x]);
  }
  return undefined;
}

function chartSeriesLabel(point: any): string | undefined {
  const series = point?.series;
  return definedText(series?.name)
    ?? definedText(series?.options?.name)
    ?? definedText(series?.userOptions?.name);
}

function definedText(value: any): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  const text = String(value).trim();
  if (!text || text === 'undefined' || text === 'null') {
    return undefined;
  }
  return text;
}

function omitUnsetParams(params: any): any {
  const clean: any = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null) {
      return;
    }
    if (typeof value === 'string' && definedText(value) === undefined) {
      return;
    }
    clean[key] = value;
  });
  return clean;
}
